import React, { useState, useRef, useCallback } from "react";
import {
  FaDownload,
  FaEnvelope,
  FaArrowLeft,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaGlobe,
} from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { useAlert } from "../../context/AlertContext";
import useAuth from "../../Hooks/useAuth";
import {
  useGenerateSimpleOfferPDFMutation,
  useSendSimpleOfferEmailMutation,
} from "../../store/storageRoles/offerApi";
import { AppLink } from "../../Components/shared";
import EditableText from "../../Components/simpleOffer/EditableText";
import OfferTable from "../../Components/simpleOffer/OfferTable";
import {
  DEFAULT_TABLE_ROWS,
  DEFAULT_CONTENT,
  calcTotals,
  formatTL,
} from "../../Components/simpleOffer/defaults";
import okkaliLogo from "../../Assets/img/okkali_logo_yazili.png";

// Firma adini yer tutucuya yerlestir
const resolveText = (template, firmaAdi) =>
  template.replace(/\{FIRMA\}/g, firmaAdi || "___________");

// ── A4 Sayfa Wrapper ──
// Her sayfa: header (logo) + content (flex-grow) + footer (en altta)
const A4_WIDTH = 794; // ~210mm at 96dpi
const A4_MIN_HEIGHT = 1123; // ~297mm at 96dpi

function PageWrapper({ children }) {
  return (
    <div
      className="mx-auto bg-white shadow-xl rounded-sm mb-8 flex flex-col overflow-hidden"
      style={{ maxWidth: A4_WIDTH, minHeight: A4_MIN_HEIGHT }}
    >
      {children}
    </div>
  );
}

function PageHeader() {
  return (
    <div className="flex justify-center pt-8 pb-4 px-12">
      <img
        src={okkaliLogo}
        alt="Okkalı"
        className="h-14 object-contain"
      />
    </div>
  );
}

function PageFooter() {
  return (
    <div className="mt-auto px-12 pb-6">
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <img
          src={okkaliLogo}
          alt="Okkalı"
          className="h-14 object-contain opacity-70"
        />
        <div className="text-right text-[10px] text-gray-500 leading-relaxed space-y-0.5">
          <div className="flex items-center justify-end gap-1">
            <span>Çınardere Mahallesi Altan Sokak No:6/1 PENDİK / İSTANBUL</span>
            <FaMapMarkerAlt className="w-2.5 h-2.5 text-gray-400 shrink-0" />
          </div>
          <div className="flex items-center justify-end gap-1">
            <span>0216 415 53 36 pbx</span>
            <FaPhoneAlt className="w-2.5 h-2.5 text-gray-400 shrink-0" />
          </div>
          <div className="flex items-center justify-end gap-1">
            <span>okkali@okkalikahve.com.tr</span>
            <MdEmail className="w-2.5 h-2.5 text-gray-400 shrink-0" />
          </div>
          <div className="flex items-center justify-end gap-1">
            <span>www.okkalikahve.com.tr</span>
            <FaGlobe className="w-2.5 h-2.5 text-gray-400 shrink-0" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SimpleOfferPage() {
  const { showSuccess, showError } = useAlert();
  const { auth } = useAuth();

  // Dokuman icerik state
  const contentRef = useRef({ ...DEFAULT_CONTENT });
  const [firmaAdi, setFirmaAdi] = useState("");
  const [rows, setRows] = useState(
    DEFAULT_TABLE_ROWS.map((r) => ({ ...r }))
  );
  const [generatePDF, { isLoading: pdfLoading }] =
    useGenerateSimpleOfferPDFMutation();
  const [sendEmailMutation, { isLoading: emailLoading }] =
    useSendSimpleOfferEmailMutation();
  const loading = pdfLoading || emailLoading;
  const [emailDrawerOpen, setEmailDrawerOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");

  const updateField = useCallback((field, value) => {
    contentRef.current[field] = value;
  }, []);

  const handleFirmaChange = useCallback(
    (value) => {
      setFirmaAdi(value);
      updateField("firmaAdi", value);
    },
    [updateField]
  );

  // Tum veriyi topla
  const collectData = useCallback(() => {
    const totals = calcTotals(rows);
    const raw = { ...contentRef.current };
    // {FIRMA} placeholder'larini firma adiyla degistir
    const textFields = ["giris", "bolum2Icerik", "bolum2IK", "tabloNot"];
    textFields.forEach((key) => {
      if (raw[key]) {
        raw[key] = raw[key].replace(/\{FIRMA\}/g, firmaAdi || "___________");
      }
    });
    return {
      ...raw,
      firmaAdi,
      rows,
      totals,
    };
  }, [firmaAdi, rows]);

  // PDF indir
  const handleDownloadPDF = async () => {
    const data = collectData();
    if (!data.firmaAdi.trim()) {
      showError("Lütfen firma adını girin.");
      return;
    }
    try {
      const result = await generatePDF({
        token: auth.accessToken,
        data,
      }).unwrap();

      const url = window.URL.createObjectURL(result);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Teklif_${data.firmaAdi.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showSuccess("PDF başarıyla indirildi.");
    } catch (err) {
      showError(
        typeof err === "string"
          ? err
          : err?.data || "PDF oluşturulurken hata oluştu."
      );
    }
  };

  // Email gonder
  const handleSendEmail = async () => {
    const data = collectData();
    if (!data.firmaAdi.trim()) {
      showError("Lütfen firma adını girin.");
      return;
    }
    if (!emailTo.trim()) {
      showError("Lütfen e-posta adresi girin.");
      return;
    }
    try {
      await sendEmailMutation({
        token: auth.accessToken,
        data,
        email: emailTo.trim(),
      }).unwrap();
      showSuccess("Teklif e-posta olarak gönderildi.");
      setEmailDrawerOpen(false);
      setEmailTo("");
    } catch (err) {
      showError(
        err?.data?.message || "E-posta gönderilirken hata oluştu."
      );
    }
  };

  const totals = calcTotals(rows);

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] sm:h-[calc(100vh-16px)] overflow-hidden">
      {/* Action Bar */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-4 py-2.5 flex items-center justify-between gap-3 z-10">
        <AppLink
          to="/Anasayfa/Teklif"
          aria-label="Geri"
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-main transition-colors"
        >
          <FaArrowLeft className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Geri</span>
        </AppLink>

        <h2 className="text-sm sm:text-base font-semibold text-gray-800">
          Sade Teklif Editörü
        </h2>

        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadPDF}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-main rounded-lg hover:bg-mainSecond disabled:opacity-50 transition-colors"
          >
            <FaDownload className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">
              {loading ? "Oluşturuluyor..." : "PDF İndir"}
            </span>
          </button>
          <button
            onClick={() => setEmailDrawerOpen(true)}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-main bg-white border border-main rounded-lg hover:bg-main/5 disabled:opacity-50 transition-colors"
          >
            <FaEnvelope className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">E-posta</span>
          </button>
        </div>
      </div>

      {/* Document Area — sayfa sayfa A4 layout */}
      <div className="flex-1 overflow-y-auto bg-gray-200">
        <div className="py-6 px-4">

          {/* ═══ SAYFA 1 ═══ */}
          <PageWrapper>
            <PageHeader />

            <div className="flex-1 px-12 sm:px-14">
              {/* Tarih */}
              <div className="mb-3 text-[13px]">
                <span className="font-bold">Tarih: </span>
                <EditableText
                  value={DEFAULT_CONTENT.tarih}
                  onUpdate={(v) => updateField("tarih", v)}
                  tag="span"
                  className="inline"
                />
              </div>

              {/* Firma Adi */}
              <div className="mb-1">
                <EditableText
                  value={firmaAdi}
                  onUpdate={handleFirmaChange}
                  className="text-[13px] font-bold italic"
                  placeholder="Firma adı girin..."
                />
              </div>

              {/* Departman */}
              <div className="mb-3">
                <EditableText
                  value={DEFAULT_CONTENT.departman}
                  onUpdate={(v) => updateField("departman", v)}
                  className="text-[13px] font-bold italic underline"
                />
              </div>

              {/* Konu */}
              <div className="mb-1 text-[13px]">
                <span className="font-bold">Konu: </span>
                <EditableText
                  value={DEFAULT_CONTENT.konu}
                  onUpdate={(v) => updateField("konu", v)}
                  tag="span"
                  className="inline"
                />
              </div>

              <p className="text-[13px] mb-3">Optimizasyon Projesi</p>

              <p className="text-[13px] mb-2">Sayın İlgili,</p>

              {/* Giris Paragrafi */}
              <div className="mb-5">
                <EditableText
                  value={resolveText(DEFAULT_CONTENT.giris, firmaAdi)}
                  onUpdate={(v) => updateField("giris", v)}
                  className="text-[13px] leading-relaxed"
                />
              </div>

              {/* Bolum 1 */}
              <h3 className="text-[13px] font-bold mb-2">
                1. İŞ MODELİ; KONSİNYE VE SABİT İSTİHKAK YAPISI
              </h3>
              <div className="mb-2">
                <EditableText
                  value={DEFAULT_CONTENT.bolum1Giris}
                  onUpdate={(v) => updateField("bolum1Giris", v)}
                  className="text-[13px] leading-relaxed"
                />
              </div>
              <ul className="list-disc pl-5 space-y-1.5 mb-5">
                <li>
                  <EditableText
                    value={DEFAULT_CONTENT.bolum1Madde1}
                    onUpdate={(v) => updateField("bolum1Madde1", v)}
                    className="text-[13px] leading-relaxed"
                  />
                </li>
                <li>
                  <EditableText
                    value={DEFAULT_CONTENT.bolum1Madde2}
                    onUpdate={(v) => updateField("bolum1Madde2", v)}
                    className="text-[13px] leading-relaxed"
                  />
                </li>
              </ul>

              {/* Bolum 2 */}
              <h3 className="text-[13px] font-bold mb-2">
                2. OPERASYON VE İNSAN KAYNAKLARI YÖNETİMİ (SLA)
              </h3>
              <div className="mb-2">
                <EditableText
                  value={resolveText(DEFAULT_CONTENT.bolum2Icerik, firmaAdi)}
                  onUpdate={(v) => updateField("bolum2Icerik", v)}
                  className="text-[13px] leading-relaxed"
                />
              </div>
              <div className="mb-4">
                <EditableText
                  value={resolveText(DEFAULT_CONTENT.bolum2IK, firmaAdi)}
                  onUpdate={(v) => updateField("bolum2IK", v)}
                  className="text-[13px] leading-relaxed"
                />
              </div>
            </div>

            <PageFooter />
          </PageWrapper>

          {/* ═══ SAYFA 2 ═══ */}
          <PageWrapper>
            <PageHeader />

            <div className="flex-1 px-12 sm:px-14">
              {/* Bolum 3 — Tablo */}
              <h3 className="text-[13px] font-bold mb-2">
                3. TİCARİ TEKLİF VE SABİT BEDEL TABLOSU
              </h3>

              <OfferTable rows={rows} onChange={setRows} />

              {/* Tablo Notu */}
              <div className="mb-2 italic">
                <EditableText
                  value={resolveText(DEFAULT_CONTENT.tabloNot, firmaAdi)}
                  onUpdate={(v) => updateField("tabloNot", v)}
                  className="text-[12px] leading-relaxed"
                />
              </div>
              <div className="mb-5">
                <EditableText
                  value={
                    DEFAULT_CONTENT.tabloOzet ||
                    `Bu Tablo ile ${totals.toplamMakineSayisi} makine ve toplam ${formatTL(totals.toplamFiyat)} aylık sabit bedel ile çözüm sunmuş oluyoruz.`
                  }
                  onUpdate={(v) => updateField("tabloOzet", v)}
                  className="text-[13px] leading-relaxed"
                />
              </div>

              {/* Bolum 4 */}
              <h3 className="text-[13px] font-bold mb-2">
                4. TİCARİ VE HUKUKİ KOŞULLAR
              </h3>
              <div className="mb-5">
                <EditableText
                  value={DEFAULT_CONTENT.bolum4Icerik}
                  onUpdate={(v) => updateField("bolum4Icerik", v)}
                  className="text-[13px] leading-relaxed"
                />
              </div>

              {/* Kapanis */}
              <div className="mb-6">
                <EditableText
                  value={DEFAULT_CONTENT.kapanis}
                  onUpdate={(v) => updateField("kapanis", v)}
                  className="text-[13px] leading-relaxed"
                />
              </div>

              {/* Imza */}
              <div className="mb-1 text-[13px]">Saygılarımla,</div>
              <div className="mb-0.5">
                <EditableText
                  value={DEFAULT_CONTENT.imzaAd}
                  onUpdate={(v) => updateField("imzaAd", v)}
                  className="text-[13px] font-bold"
                />
              </div>
              <div className="mb-0.5">
                <EditableText
                  value={DEFAULT_CONTENT.imzaGorev}
                  onUpdate={(v) => updateField("imzaGorev", v)}
                  className="text-[13px]"
                />
              </div>
              <div className="mb-4">
                <EditableText
                  value={DEFAULT_CONTENT.imzaUnvan}
                  onUpdate={(v) => updateField("imzaUnvan", v)}
                  className="text-[13px] font-bold"
                />
              </div>
            </div>

            <PageFooter />
          </PageWrapper>

        </div>
      </div>

      {/* Email Drawer */}
      {emailDrawerOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="flex-1 bg-black/40"
            onClick={() => setEmailDrawerOpen(false)}
          />
          <div className="w-full max-w-md bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-800">
                Teklifi E-posta ile Gönder
              </h3>
              <button
                onClick={() => setEmailDrawerOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                &times;
              </button>
            </div>
            <div className="flex-1 p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alıcı E-posta
                </label>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="ornek@firma.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-main/20 focus:border-main outline-none"
                />
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                <div className="font-medium mb-1">Teklif Özeti:</div>
                <div>Firma: {firmaAdi || "-"}</div>
                <div>Toplam Makine: {calcTotals(rows).toplamMakineSayisi}</div>
                <div>
                  Toplam Bedel: {formatTL(calcTotals(rows).toplamFiyat)}
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-gray-200">
              <button
                onClick={handleSendEmail}
                disabled={loading || !emailTo.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-main rounded-lg hover:bg-mainSecond disabled:opacity-50 transition-colors"
              >
                <FaEnvelope className="w-4 h-4" />
                {loading ? "Gönderiliyor..." : "Gönder"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
